import React, { useEffect, useRef, useState } from "react"
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Collapse,
  Form,
  FormGroup,
  Label,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Table,
} from "reactstrap"
import PropTypes from "prop-types"
import { withTranslation } from "react-i18next"
import {
  Divider,
  Drawer,
  Switch,
  Input as AntdInput,
  Table as AntdTable,
  Form as AntdForm,
  Select,
  Tooltip,
} from "antd"
import * as Yup from "yup"
import { useFormik } from "formik"
import {
  generateStrongPassword,
  getLottieLoadingOptions,
  showToast,
  checkboxFormatter,
  formatDateHoursMinutes,
  defaultPageSizeOptions,
  stringSorter,
} from "utils/general"
import {
  getAdminsPrivateClients,
  linkPrivateClients,
  sendLoginCredentials,
  unlinkPrivateClients,
  getGroups,
} from "helpers/backend_helper"
import Lottie from "react-lottie"
import classnames from "classnames"
import TagsTree from "components/DropdownList/TagsTree"
import withRouter from "components/Common/withRouter"
import {
  addGroupsToUser,
  deleteKeycloakUser,
  getKeycloakUser,
  getUsersEvents,
  listUsersGroups,
  removeGroupsFromUser,
  setTemporaryUserPassword,
  updateKeycloakUser,
} from "helpers/keycloak_api_helper"
import SimpleBar from "simplebar-react"
import { tableDefaultHeight } from "constants/layout"

const { Search } = AntdInput

const KeycloakUserDrawer = props => {
  const [activeTab, setActiveTab] = useState("1")
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [update, setUpdate] = useState(false)
  const [loading, setLoading] = useState(false)
  const [ongoingDelete, setOngoingDelete] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [userData, setUserData] = useState(null)
  const [newPassword, setNewPassword] = useState("")
  const [groups, setGroups] = useState([])
  const [groupsOrig, setGroupsOrig] = useState([])
  const [privateClients, setPrivateClients] = useState([])
  const [filteredPrivateClients, setFilteredPrivateClients] = useState([])
  const [filteredGroups, setFilteredGroups] = useState([])
  const [selectedGroups, setSelectedGroups] = useState([])
  const [selectedClients, setSelectedClients] = useState([])
  const [searchText, setSearchText] = useState("")
  const [searchText2, setSearchText2] = useState("")
  const [showAllClients, setShowAllClients] = useState(false)
  const [tempOptions, setTempOptions] = useState({})
  const [groupsLoading, setGroupsLoading] = useState(false)
  const [options, setOptions] = useState([])
  const [origOptions, setOrigOptions] = useState([])
  const [selectedTags, setSelectedTags] = useState([])
  const [temp, setTemp] = useState([])
  const [sessions, setSessions] = useState([])

  const abortRef = useRef(null)
  useEffect(() => {
    abortRef.current = new AbortController()
    return () => abortRef.current.abort()
  }, [])

  useEffect(() => {
    if (props.selectedRow) {
      setNewPassword("")
      getData()
    }
  }, [props.selectedRow])

  useEffect(() => {
    if (activeTab == "2" && props.selectedRow && groups.length == 0) {
      getGroupsList()
      getDataArr()
    } else if (
      activeTab == "3" &&
      props.selectedRow &&
      privateClients.length == 0
    ) {
      getPrivateClients()
    }
  }, [activeTab])

  useEffect(() => {
    if (activeTab == "3" && props.selectedRow) {
      getPrivateClients()
    }
  }, [showAllClients])

  useEffect(() => {
    if (!props.showDrawer) {
      setGroups([])
      setGroupsOrig([])
      setPrivateClients([])
      setActiveTab("1")
    }
  }, [props.showDrawer])

  // Search or filter changed
  useEffect(() => {
    handleSearch()
  }, [searchText, groups])

  useEffect(() => {
    if (selectedTags.length > 0) {
      setGroups(tempOptions)
    }
    if (selectedTags.length == 0) {
      setGroups(groupsOrig)
    }
  }, [tempOptions])

  useEffect(() => {
    if (options.length > 0) {
      let arr = []
      setTempOptions(arr)
      for (const f of groupsOrig) {
        let g = f.group
        for (const element of options) {
          if (g == element.name) {
            arr.push(f)
          }
        }
      }
      setTempOptions(arr)
    }
    if (options.length == 0) {
      setTempOptions(groupsOrig)
    }
  }, [options])

  useEffect(() => {
    handlePrivatesSearch()
  }, [searchText2, privateClients])

  // Handle search function
  const handleSearch = () => {
    let result = groups.filter(data =>
      data.group?.toLowerCase().includes(searchText.toLowerCase())
    )
    setFilteredGroups(result)
  }

  // Handle search function
  const handlePrivatesSearch = () => {
    let result = privateClients.filter(
      data =>
        data.group?.toLowerCase().includes(searchText2.toLowerCase()) ||
        data.name?.toLowerCase().includes(searchText2.toLowerCase())
    )
    setFilteredPrivateClients(result)
  }

  // Get admins data
  const getData = () => {
    setUserData(null)
    if (props.selectedRow?.id) {
      setLoading(true)
      getKeycloakUser(abortRef.current, props.selectedRow.id)
        .then(result => {
          //console.log(result)
          if (result.id) {
            if (result.attributes) {
              result.description = result.attributes.description
                ? result.attributes.description[0]
                : ""
              result.gsm = result.attributes.gsm ? result.attributes.gsm[0] : ""
              result.locale = result.attributes.locale
                ? result.attributes.locale[0]
                : ""
              result.videoRoomId = result.attributes.videoRoomId
                ? result.attributes.videoRoomId[0]
                : ""
              result.videoRoomDemoId = result.attributes.videoRoomDemoId
                ? result.attributes.videoRoomDemoId[0]
                : ""
              result.videoNickname = result.attributes.videoNickname
                ? result.attributes.videoNickname[0]
                : ""
              result.alarmCenterType = result.attributes.alarmCenterType
                ? result.attributes.alarmCenterType[0]
                : ""
              result.supportType = result.attributes.supportType
                ? result.attributes.supportType[0]
                : ""
              result.unit = result.attributes.unit
                ? result.attributes.unit[0]
                : ""
            }
            setUserData(result)
          }
        })
        .catch(err => {
          console.log(err)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  // Get sessions
  const getSessions = () => {
    if (userData?.id) {
      getUsersEvents(abortRef.current, userData.id)
        .then(result => {
          console.log(result)
          if (Array.isArray(result)) {
            setSessions(result)
          }
        })
        .catch(err => {
          console.log(err)
        })
        .finally(() => {})
    }
  }

  // Get groups
  const getDataArr = () => {
    if (!groupsLoading) {
      setGroupsLoading(true)
      getGroups(abortRef.current)
        .then(result => {
          if (result.entries) {
            let opts = []
            let counter = 1
            for (const group of result.entries) {
              let groupObject = new Object()
              groupObject.id = counter++
              groupObject.name = group.name
              groupObject.value = group.name
              groupObject.tag = settingToString(group.tags)
              opts.push(groupObject)
            }
            setOrigOptions(opts)
          } else {
            setOrigOptions([])
          }
        })
        .catch(e => {
          console.log(e)
        })
        .finally(() => {
          setGroupsLoading(false)
        })
    }
  }

  // Get admins groups
  const getGroupsList = () => {
    listUsersGroups(abortRef.current, props.selectedRow.id)
      .then(result => {
        //console.log("GROUPS: ", result)
        if (Array.isArray(result)) {
          setGroups(result)
          setGroupsOrig(result)
        } else {
          showToast(props.t("Error"), "error")
        }
      })
      .catch(err => {
        console.log(err)
        showToast(props.t("Error"), "error")
      })
      .finally(() => {})
  }

  useEffect(() => {
    // console.log(selectedTags, "tags")
    if (selectedTags?.length > 0) {
      setTemp([])
      for (const o of origOptions) {
        for (const tag of selectedTags) {
          if (o.tag.includes(tag.toString())) {
            setTemp(filtered => [...filtered, o])
          }
        }
      }
    }
    if (selectedTags?.length == 0) {
      setOptions([])
      setGroups(groupsOrig)
    }
  }, [selectedTags])

  // Filter by selected tags
  useEffect(() => {
    let setNew = Array.from(new Set(temp))
    setOptions(setNew)
    // console.log(setNew, "new")
  }, [temp])

  // Set tags to string
  const settingToString = arr => {
    let string = []
    if (arr.length > 0) {
      for (const g of arr) {
        let tag = g.key
        string.push(tag)
      }
    }
    string = string.toString()
    return string
  }

  // Get private clients
  const getPrivateClients = () => {
    getAdminsPrivateClients(
      abortRef.current,
      props.selectedRow.username,
      showAllClients
    )
      .then(result => {
        if (result.statusCode == 200 && result.clients) {
          result.clients.sort((a, b) => (a.name < b.name ? 1 : -1))
          result.clients.sort((a, b) => (a.group > b.group ? 1 : -1))
          setPrivateClients(result.clients)
        } else {
          showToast(props.t("Error"), "error")
        }
      })
      .catch(err => {
        console.log(err)
        showToast(props.t("Error"), "error")
      })
      .finally(() => {})
  }

  // Link private clients
  const linkClients = () => {
    if (!update && selectedClients.length > 0) {
      setUpdate(true)
      linkPrivateClients(
        abortRef.current,
        props.selectedRow.username,
        selectedClients
      )
        .then(result => {
          if (result.statusCode == 200) {
            showToast(props.t("Success"), "success")
            getPrivateClients()
            setSelectedClients([])
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setUpdate(false)
        })
    }
  }

  // Unlink private clients
  const unlinkClients = () => {
    if (!update && selectedClients.length > 0) {
      setUpdate(true)
      unlinkPrivateClients(
        abortRef.current,
        props.selectedRow.username,
        selectedClients
      )
        .then(result => {
          if (result.statusCode == 200) {
            showToast(props.t("Success"), "success")
            getPrivateClients()
            setSelectedClients([])
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setUpdate(false)
        })
    }
  }

  // Update client validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      description: userData?.description || "",
      firstName: userData?.firstName || "",
      lastName: userData?.lastName || "",
      gsm: userData?.gsm || "",
      email: userData?.email || "",
      emailVerified: userData?.emailVerified || false,
      locale: userData?.locale || "",
      videoRoomId: userData?.videoRoomId || "",
      videoRoomDemoId: userData?.videoRoomDemoId || "",
      videoNickname: userData?.videoNickname || "",
      alarmCenterType: userData?.alarmCenterType || "",
      basicUser: userData?.basicUser || false,
      alarmMonitoring: userData?.alarmMonitoring || false,
      appointments: userData?.appointments || false,
      groupMessages: userData?.groupMessages || false,
      videoSystem: userData?.videoSystem || false,
      videoAppSettings: userData?.videoAppSettings || false,
      enabled: userData?.enabled || false,
      supportType: userData?.supportType || "0",
      unit: userData?.unit || "",
    },
    validationSchema: Yup.object({
      videoNickname: Yup.string()
        .matches(
          /^[a-zA-Z0-9\s\-()!?&.,+#äöåøæÄÖÅÆØ]*$/,
          props.t("ContainsNotAllowedCharacters")
        )
        .trim(),
      firstName: Yup.string()
        .matches(
          /^[a-zA-Z0-9\s\-()!?&.,+#äöåøæÄÖÅÆØ]*$/,
          props.t("ContainsNotAllowedCharacters")
        )
        .trim()
        .required(props.t("Required field")),
      lastName: Yup.string()
        .matches(
          /^[a-zA-Z0-9\s\-()!?&.,+#äöåøæÄÖÅÆØ]*$/,
          props.t("ContainsNotAllowedCharacters")
        )
        .trim()
        .required(props.t("Required field")),
    }),
    onSubmit: values => {
      console.log("Submit values: ", values)
      setSuccess("")
      setError("")
      if (userData?.id && !update) {
        let temp = structuredClone(values)

        // Fallback video nickname
        if (!temp.videoNickname) {
          temp.videoNickname = `${temp.firstName} ${temp.lastName}`
        }

        temp.supportType = Number.parseInt(values.supportType, 10)
        if (!temp.locale) temp.locale = "fi"

        setUpdate(true)
        updateKeycloakUser(abortRef.current, userData.id, temp)
          .then(result => {
            if (result == "OK") {
              setSuccess("Updated")
              getData()
              props.getAdmins()
            } else {
              setError("Error")
            }
          })
          .catch(err => {
            console.log(err)
            setError("Error")
          })
          .finally(() => {
            setUpdate(false)
          })
      }
    },
  })

  // Delete
  const deleteAdmin = () => {
    if (props.selectedRow?.id) {
      setOngoingDelete(true)
      deleteKeycloakUser(abortRef.current, props.selectedRow?.id)
        .then(result => {
          if (result == "OK") {
            setDeleteModal(false)
            props.setShowDrawer(false)
            showToast(props.t("Deleted"), "success")
            props.getAdmins()
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setOngoingDelete(false)
        })
    }
  }

  // Send credentials
  // Update Keycloak user first, then send data to TW
  const sendCredentials = () => {
    if (
      userData?.username &&
      userData?.id &&
      newPassword?.length >= 14 &&
      !update
    ) {
      setUpdate(true)
      setTemporaryUserPassword(abortRef.current, userData.id, newPassword)
        .then(result => {
          if (result == "OK") {
            sendLoginCredentials(
              abortRef.current,
              userData.username,
              newPassword,
              userData.email,
              userData.gsm,
              userData.locale ?? "fi"
            )
              .then(result => {
                if (result.statusCode == 200) {
                  showToast(props.t("Sent"), "success")
                  getData()
                } else {
                  showToast(props.t("Error"), "error")
                }
              })
              .catch(err => {
                console.log(err)
                showToast(props.t("Error"), "error")
              })
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setUpdate(false)
        })
    }
  }

  // Link user to selected groups
  const linkGroups = () => {
    if (props.selectedRow?.id && !update && selectedGroups.length > 0) {
      setUpdate(true)
      addGroupsToUser(abortRef.current, props.selectedRow.id, selectedGroups)
        .then(result => {
          if (result == "OK") {
            showToast(props.t("Success"), "success")
            setSelectedGroups([])
            getGroupsList()
            props.getAdmins()
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setUpdate(false)
        })
    }
  }

  // Unlink user from selected groups
  const unlinkGroups = () => {
    if (props.selectedRow?.id && !update && selectedGroups.length > 0) {
      setUpdate(true)
      removeGroupsFromUser(
        abortRef.current,
        props.selectedRow.id,
        selectedGroups
      )
        .then(result => {
          if (result == "OK") {
            showToast(props.t("Success"), "success")
            setSelectedGroups([])
            getGroupsList()
            props.getAdmins()
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setUpdate(false)
        })
    }
  }

  // Table structure
  const columns = [
    {
      dataIndex: "linked",
      title: props.t("Linked"),
      align: "center",
      render: checkboxFormatter,
      defaultSortOrder: "descend",
      sorter: (a, b) => a.linked - b.linked,
      width: "100px",
      ellipsis: true,
    },
    {
      dataIndex: "group",
      title: props.t("Group"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "group", sortOrder)
      },
      width: "1000px",
      ellipsis: true,
    },
  ]

  const columns1 = [
    {
      dataIndex: "linked",
      title: props.t("Linked"),
      align: "center",
      defaultSortOrder: "descend",
      render: checkboxFormatter,
      sorter: (a, b) => a.linked - b.linked,
      width: "100px",
      ellipsis: true,
    },
    {
      dataIndex: "group",
      title: props.t("Group"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "group", sortOrder)
      },
      width: "100px",
      ellipsis: true,
    },
    {
      dataIndex: "name",
      title: props.t("Name"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "name", sortOrder)
      },
      width: "1000px",
      ellipsis: true,
    },
  ]

  const selectRow = {
    selectedRowKeys: selectedGroups,
    onSelect: (row, isSelect, rowIndex, e) => {
      if (isSelect) {
        setSelectedGroups(prevState => [...prevState, row.id])
      } else {
        setSelectedGroups(prevState =>
          prevState.filter(prevItem => prevItem !== row.id)
        )
      }
    },
    onSelectAll: (isSelect, rows, e) => {
      if (isSelect) {
        rows.map(row => setSelectedGroups(prevState => [...prevState, row.id]))
      } else {
        setSelectedGroups([])
      }
    },
  }
  const selectRow2 = {
    selectedRowKeys: selectedClients,
    onSelect: (row, isSelect, rowIndex, e) => {
      // console.log(isSelect, row, e)
      if (isSelect) {
        setSelectedClients(prevState => [...prevState, row.key])
      } else {
        setSelectedClients(prevState =>
          prevState.filter(prevItem => prevItem !== row.key)
        )
      }
    },
    onSelectAll: (isSelect, rows, e) => {
      // console.log(isSelect, rows, e)
      if (isSelect) {
        rows.map(row =>
          setSelectedClients(prevState => [...prevState, row.key])
        )
      } else {
        setSelectedClients([])
      }
    },
  }

  return (
    <>
      <Drawer
        title={props.selectedRow?.username}
        width={"80%"}
        placement="right"
        onClose={() => {
          props.setShowDrawer(false)
          setError("")
          setSuccess("")
        }}
        open={props.showDrawer}
        zIndex={1003}
        // //bodyStyle={{ backgroundColor: "#f8f8f8" }}
      >
        <div className="d-flex flex-wrap mb-3">
          <ul className="nav icon-tab">
            <NavItem>
              <NavLink
                to="#"
                className={classnames({ active: activeTab === "1" })}
                onClick={() => {
                  setActiveTab("1")
                }}
              >
                {props.t("User information")}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "2" })}
                onClick={() => {
                  setActiveTab("2")
                }}
              >
                {props.t("Groups")}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "3" })}
                onClick={() => {
                  setActiveTab("3")
                }}
              >
                {props.t("Private clients")}
              </NavLink>
            </NavItem>
          </ul>
        </div>

        <TabContent activeTab={activeTab} className="">
          <TabPane className="show" tabId="1">
            <Row xs={1} xl={2}>
              <Col>
                <Card>
                  <CardBody>
                    <CardTitle className="mb-4">
                      {props.t("Information")}
                    </CardTitle>
                    {loading && (
                      <Lottie
                        options={getLottieLoadingOptions()}
                        height={100}
                        width={100}
                      ></Lottie>
                    )}
                    {!loading && (
                      <Form
                        className="form-horizontal"
                        onSubmit={e => {
                          e.preventDefault()
                          return false
                        }}
                      >
                        {error ? (
                          <Alert color="danger">{props.t(error)}</Alert>
                        ) : null}
                        {success ? (
                          <Alert color="success">{props.t(success)}</Alert>
                        ) : null}
                        <Row>
                          <Col>
                            <FormGroup>
                              <Label>{props.t("Description")}</Label>
                              <AntdInput
                                allowClear
                                placeholder={props.t("Description")}
                                value={validation.values.description}
                                onChange={v => {
                                  validation.setFieldValue(
                                    "description",
                                    v.target.value
                                  )
                                }}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row xs={1} md={2}>
                          <Col>
                            <FormGroup>
                              <Label>{props.t("First name")}</Label>
                              <AntdForm.Item
                                validateStatus={
                                  validation.errors.firstName ? "error" : ""
                                }
                                hasFeedback
                                help={validation.errors.firstName}
                              >
                                <AntdInput
                                  allowClear
                                  placeholder={props.t("First name")}
                                  value={validation.values.firstName}
                                  onChange={v => {
                                    validation.setFieldValue(
                                      "firstName",
                                      v.target.value
                                    )
                                  }}
                                />
                              </AntdForm.Item>
                            </FormGroup>
                          </Col>
                          <Col>
                            <FormGroup>
                              <Label>{props.t("Last name")}</Label>
                              <AntdForm.Item
                                validateStatus={
                                  validation.errors.lastName ? "error" : ""
                                }
                                hasFeedback
                                help={validation.errors.lastName}
                              >
                                <AntdInput
                                  allowClear
                                  placeholder={props.t("Last name")}
                                  value={validation.values.lastName}
                                  onChange={v => {
                                    validation.setFieldValue(
                                      "lastName",
                                      v.target.value
                                    )
                                  }}
                                />
                              </AntdForm.Item>
                            </FormGroup>
                          </Col>
                          <Col>
                            <FormGroup>
                              <Label>{props.t("GSM")}</Label>
                              <AntdInput
                                allowClear
                                placeholder={props.t("GSM")}
                                value={validation.values.gsm}
                                onChange={v => {
                                  validation.setFieldValue(
                                    "gsm",
                                    v.target.value
                                  )
                                }}
                              />
                            </FormGroup>
                          </Col>
                          <Col>
                            <FormGroup>
                              <Label>{props.t("Email")}</Label>
                              <AntdInput
                                allowClear
                                placeholder={props.t("Email")}
                                value={validation.values.email}
                                onChange={v => {
                                  validation.setFieldValue(
                                    "email",
                                    v.target.value
                                  )
                                }}
                              />
                            </FormGroup>
                          </Col>
                          <Col>
                            <FormGroup>
                              <Label>{props.t("Unit info")}</Label>
                              <AntdInput
                                allowClear
                                placeholder={props.t("Unit info")}
                                value={validation.values.unit}
                                onChange={v => {
                                  validation.setFieldValue(
                                    "unit",
                                    v.target.value
                                  )
                                }}
                              />
                            </FormGroup>
                          </Col>
                          <Col>
                            <FormGroup>
                              <Label>{props.t("Language")}</Label>
                              <Select
                                value={validation.values.locale}
                                placeholder={props.t("Choose")}
                                onChange={v => {
                                  validation.setFieldValue("locale", v)
                                }}
                                options={[
                                  { label: props.t("Undefined"), value: "" },
                                  { label: props.t("English"), value: "en" },
                                  { label: props.t("Finnish"), value: "fi" },
                                  { label: props.t("Swedish"), value: "sv" },
                                ]}
                                style={{ width: 100 + "%" }}
                              />
                            </FormGroup>
                          </Col>
                          <Col>
                            <FormGroup>
                              <Label>{props.t("Support")}</Label>
                              <Select
                                placeholder={props.t("Choose")}
                                value={validation.values.supportType}
                                onChange={v => {
                                  validation.setFieldValue("supportType", v)
                                }}
                                options={[
                                  { label: props.t("Default"), value: "0" },
                                  {
                                    label: props.t("Suvanto extended support"),
                                    value: "3",
                                  },
                                  { label: props.t("Elisa"), value: "1" },
                                  {
                                    label: props.t("Elisa extended support"),
                                    value: "4",
                                  },
                                  { label: props.t("2M-IT"), value: "2" },
                                  {
                                    label: props.t("2M-IT extended support"),
                                    value: "5",
                                  },
                                  {
                                    label: props.t("Istekki-Pirha"),
                                    value: "6",
                                  },
                                  {
                                    label: props.t("Istekki-PSHVA"),
                                    value: "7",
                                  },
                                ]}
                                style={{ width: 100 + "%" }}
                              />
                            </FormGroup>
                          </Col>
                          <Col>
                            <FormGroup>
                              <Label>{props.t("Alarm center")}</Label>
                              <Select
                                placeholder={props.t("Choose")}
                                value={validation.values.alarmCenterType}
                                onChange={v => {
                                  validation.setFieldValue("alarmCenterType", v)
                                }}
                                options={[
                                  { label: props.t("Undefined"), value: "" },
                                  { label: props.t("Test"), value: "Test" },
                                  { label: props.t("K2"), value: "K2" },
                                  {
                                    label: props.t("PNC AddSecure"),
                                    value: "PNC",
                                  },
                                  {
                                    label: props.t("PNC Essote"),
                                    value: "PNC_Essote",
                                  },
                                  { label: props.t("Avarn"), value: "Avarn" },
                                  { label: props.t("Verifi"), value: "Verifi" },
                                ]}
                                style={{ width: 100 + "%" }}
                              />
                            </FormGroup>
                          </Col>
                          <Col>
                            <AntdForm layout="vertical">
                              <AntdForm.Item
                                label={props.t("Video call display name")}
                                tooltip={props.t(
                                  "If a display name is not set, the first and last name will be used."
                                )}
                                validateStatus={
                                  validation.errors.videoNickname ? "error" : ""
                                }
                                hasFeedback
                                help={validation.errors.videoNickname}
                              >
                                <AntdInput
                                  allowClear
                                  placeholder={props.t(
                                    "Video call display name"
                                  )}
                                  value={validation.values.videoNickname}
                                  onChange={v => {
                                    validation.setFieldValue(
                                      "videoNickname",
                                      v.target.value
                                    )
                                  }}
                                />
                              </AntdForm.Item>
                            </AntdForm>
                          </Col>
                          <Col>
                            <FormGroup>
                              <Label>{props.t("Group host number")}</Label>
                              <AntdInput
                                allowClear
                                placeholder={props.t("Video number")}
                                value={validation.values.videoRoomId}
                                onChange={v => {
                                  validation.setFieldValue(
                                    "videoRoomId",
                                    v.target.value
                                  )
                                }}
                              />
                            </FormGroup>
                          </Col>
                          <Col>
                            <FormGroup>
                              <Label>
                                {props.t("Group host number") + " (Demo)"}
                              </Label>
                              <AntdInput
                                allowClear
                                placeholder={props.t("Video number")}
                                value={validation.values.videoRoomDemoId}
                                onChange={v => {
                                  validation.setFieldValue(
                                    "videoRoomDemoId",
                                    v.target.value
                                  )
                                }}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row xs={1} md={2}>
                          <Col>
                            <FormGroup className="mt-2">
                              <Switch
                                className="me-2"
                                name="emailVerified"
                                checked={validation.values.emailVerified}
                                onChange={v => {
                                  validation.setFieldValue("emailVerified", v)
                                }}
                              ></Switch>
                              <Label>{props.t("Email verified")}</Label>
                            </FormGroup>
                          </Col>
                          <Col>
                            <FormGroup className="mt-2">
                              <Switch
                                className="me-2"
                                name="enabled"
                                checked={validation.values.enabled}
                                onChange={v => {
                                  validation.setFieldValue("enabled", v)
                                }}
                              ></Switch>
                              <Label>{props.t("Enabled")}</Label>
                            </FormGroup>
                          </Col>
                        </Row>
                        <Divider
                          orientation="left"
                          className="font-size-14 mt-1 text-primary"
                          orientationMargin="0"
                        >
                          {props.t("Permissions")}
                        </Divider>
                        <Row xs={1} md={2}>
                          <Col>
                            <FormGroup className="mt-2">
                              <Switch
                                className="me-2"
                                name="basicUser"
                                checked={validation.values.basicUser}
                                onChange={v => {
                                  validation.setFieldValue("basicUser", v)
                                }}
                              ></Switch>
                              <Label>{props.t("Professional")}</Label>
                            </FormGroup>
                          </Col>
                          <Col>
                            <FormGroup className="mt-2">
                              <Switch
                                className="me-2"
                                name="alarmMonitoring"
                                checked={validation.values.alarmMonitoring}
                                onChange={v => {
                                  validation.setFieldValue("alarmMonitoring", v)
                                }}
                              ></Switch>
                              <Label>{props.t("Alarm monitoring")}</Label>
                            </FormGroup>
                          </Col>
                          <Col>
                            <FormGroup className="mt-2">
                              <Switch
                                className="me-2"
                                name="appointments"
                                checked={validation.values.appointments}
                                onChange={v => {
                                  validation.setFieldValue("appointments", v)
                                }}
                              ></Switch>
                              <Label>{props.t("Appointments")}</Label>
                            </FormGroup>
                          </Col>
                          <Col>
                            <FormGroup className="mt-2">
                              <Switch
                                className="me-2"
                                name="videoSystem"
                                checked={validation.values.videoSystem}
                                onChange={v => {
                                  validation.setFieldValue("videoSystem", v)
                                }}
                              ></Switch>
                              <Label>{props.t("Video system")}</Label>
                            </FormGroup>
                          </Col>
                          <Col>
                            <FormGroup className="mt-2">
                              <Switch
                                className="me-2"
                                name="videoAppSettings"
                                checked={validation.values.videoAppSettings}
                                onChange={v => {
                                  validation.setFieldValue(
                                    "videoAppSettings",
                                    v
                                  )
                                }}
                              ></Switch>
                              <Label>{props.t("Video app settings")}</Label>
                            </FormGroup>
                          </Col>
                          <Col>
                            <FormGroup className="mt-2">
                              <Switch
                                className="me-2"
                                name="groupMessages"
                                checked={validation.values.groupMessages}
                                onChange={v => {
                                  validation.setFieldValue("groupMessages", v)
                                }}
                              ></Switch>
                              <Label>{props.t("Group messages")}</Label>
                            </FormGroup>
                          </Col>
                        </Row>
                        <Divider className="mt-0"></Divider>

                        {userData?.requiredActions?.length > 0 && (
                          <Alert color={"info"}>
                            <span>
                              {props.t("Required user actions") +
                                ": " +
                                userData?.requiredActions.join(", ")}
                            </span>
                          </Alert>
                        )}

                        <div className="mt-2 mb-3 d-flex justify-content-center">
                          <Button
                            color="primary"
                            disabled={update}
                            style={{ minWidth: "150px" }}
                            onClick={() => {
                              validation.handleSubmit()
                            }}
                          >
                            {props.t("Save")}
                          </Button>
                        </div>
                      </Form>
                    )}
                  </CardBody>
                </Card>
              </Col>
              <Col>
                <Card>
                  <CardBody>
                    <CardTitle className="mb-4">{props.t("Actions")}</CardTitle>
                    <ListGroup className="list-group-flush">
                      <ListGroupItem className="pb-4">
                        <Row>
                          <Col>
                            <div>
                              <h6>{props.t("Send login credentials")}</h6>
                              <span className="text-muted">
                                {props.t("Email & GSM required")}
                              </span>
                              <AntdForm.Item
                                className="mt-2 mb-1"
                                style={{ maxWidth: "280px" }}
                              >
                                <Search
                                  allowClear
                                  enterButton={
                                    <Tooltip
                                      title={props.t("Generate new password")}
                                      key="genPwButton"
                                      placement="bottom"
                                    >
                                      <i className="mdi mdi-shield-refresh font-size-16" />
                                    </Tooltip>
                                  }
                                  onSearch={() => {
                                    let pw = generateStrongPassword(14)
                                    setNewPassword(pw)
                                  }}
                                  placeholder={props.t("Set new password")}
                                  value={newPassword}
                                  onChange={v => {
                                    setNewPassword(v.target.value)
                                  }}
                                />
                              </AntdForm.Item>
                            </div>
                          </Col>
                          <Col className="col-auto">
                            <Button
                              color="primary"
                              style={{ minWidth: "100px" }}
                              disabled={
                                update ||
                                !userData?.email ||
                                !userData?.gsm ||
                                newPassword.length < 14
                              }
                              onClick={() => {
                                sendCredentials()
                              }}
                            >
                              {props.t("Send")}
                            </Button>
                          </Col>
                        </Row>
                      </ListGroupItem>
                      <ListGroupItem className="pb-4">
                        <Row>
                          <Col>
                            <div>
                              <h6>{props.t("Set temporary password")}</h6>
                              <span className="text-muted">
                                {props.t("SetTemporaryPasswordDescription")}
                              </span>
                              <AntdForm.Item
                                className="mt-2 mb-1"
                                style={{ maxWidth: "280px" }}
                              >
                                <Search
                                  allowClear
                                  enterButton={
                                    <Tooltip
                                      title={props.t("Generate new password")}
                                      key="genPwButton2"
                                      placement="bottom"
                                    >
                                      <i className="mdi mdi-shield-refresh font-size-16" />
                                    </Tooltip>
                                  }
                                  onSearch={() => {
                                    let pw = generateStrongPassword(14)
                                    setNewPassword(pw)
                                  }}
                                  placeholder={props.t("Set new password")}
                                  value={newPassword}
                                  onChange={v => {
                                    setNewPassword(v.target.value)
                                  }}
                                />
                              </AntdForm.Item>
                            </div>
                          </Col>
                          <Col className="col-auto">
                            <Button
                              color="primary"
                              style={{ minWidth: "100px" }}
                              disabled={
                                update ||
                                loading ||
                                !userData?.id ||
                                newPassword.length < 14
                              }
                              onClick={() => {
                                if (
                                  !update &&
                                  userData?.id &&
                                  newPassword?.length >= 14
                                ) {
                                  setUpdate(true)
                                  setTemporaryUserPassword(
                                    abortRef.current,
                                    userData.id,
                                    newPassword
                                  )
                                    .then(result => {
                                      if (result == "OK") {
                                        showToast(
                                          props.t("Password set"),
                                          "success"
                                        )
                                        getData()
                                      } else {
                                        showToast(props.t("Error"), "error")
                                      }
                                    })
                                    .catch(err => {
                                      console.log(err)
                                      showToast(props.t("Error"), "error")
                                    })
                                    .finally(() => {
                                      setUpdate(false)
                                    })
                                }
                              }}
                            >
                              {props.t("Set")}
                            </Button>
                          </Col>
                        </Row>
                      </ListGroupItem>
                      <ListGroupItem className="pb-4">
                        <Row>
                          <Col>
                            <div>
                              <h6>{props.t("View SMS history")}</h6>
                              <span className="text-muted">
                                {props.t("Opens in a new window")}
                              </span>
                            </div>
                          </Col>
                          <Col className="col-auto">
                            <Button
                              color="secondary"
                              style={{ minWidth: "100px" }}
                              disabled={!userData?.gsm}
                              onClick={() => {
                                globalThis.open(
                                  "/logs/sms?recipient=" + userData?.gsm,
                                  "_blank",
                                  "noreferrer"
                                )
                              }}
                            >
                              {props.t("OpenCommand")}
                            </Button>
                          </Col>
                        </Row>
                      </ListGroupItem>
                      <ListGroupItem className="pb-4">
                        <Row>
                          <Col>
                            <div>
                              <h6>{props.t("Deletion")}</h6>
                              <span className="text-muted">
                                {props.t("Deletes the user permanently")}
                              </span>
                            </div>
                          </Col>
                          <Col className="col-auto">
                            <Button
                              color="danger"
                              style={{ minWidth: "100px" }}
                              disabled={
                                ongoingDelete || loading || !userData?.id
                              }
                              onClick={() => {
                                setDeleteModal(true)
                              }}
                            >
                              {props.t("Delete")}
                            </Button>
                          </Col>
                        </Row>
                      </ListGroupItem>
                    </ListGroup>
                  </CardBody>
                </Card>
              </Col>
              {/** Sessions: not used yet */}
              <Col className="d-none">
                <Card>
                  <CardBody>
                    <CardTitle className="mb-4">
                      <Row>
                        <Col>{props.t("Sessions")}</Col>
                        <Col className="col-auto">
                          <Button
                            color="primary"
                            size="sm"
                            className=""
                            onClick={() => {
                              getSessions()
                            }}
                          >
                            <i className="me-2 mdi mdi-refresh text-truncate align-middle"></i>
                            <span className="">{props.t("Search")}</span>
                          </Button>
                        </Col>
                      </Row>
                    </CardTitle>

                    <SimpleBar style={{ maxHeight: "70vh" }}>
                      <Table
                        style={{ tableLayout: "fixed" }}
                        className="table"
                        bordered={false}
                        striped={false}
                        hover={true}
                      >
                        <thead>
                          <tr>
                            <th>{props.t("Time")}</th>
                            <th>{props.t("Type")}</th>
                            <th>{props.t("IP")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sessions.map((session, index) => {
                            return (
                              <tr key={session.id}>
                                <td scope="row">
                                  {formatDateHoursMinutes(session.time)}
                                </td>
                                <td scope="row">{session.type}</td>
                                <td>{session.ipAddress}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </Table>
                    </SimpleBar>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </TabPane>

          <TabPane className="show" tabId="2">
            <Card>
              <CardBody>
                <React.Fragment>
                  <Row>
                    <Col className="col-auto pe-0">
                      <div
                        className="d-inline-block mt-0 me-3"
                        style={{ minWidth: "300px" }}
                      >
                        <TagsTree
                          disabled={groupsLoading}
                          loadData={true}
                          expanded={true}
                          selectionChanged={setSelectedTags}
                        />
                      </div>
                      <div className="search-box me-2 mb-2 mt-0 d-inline-block float-end">
                        <div className="position-relative">
                          <Search
                            placeholder={props.t("Search")}
                            allowClear
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            style={{ width: "180px" }}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col>
                      <div className="mt-2 float-end">
                        <p>
                          {filteredGroups.length} {props.t("groups")}
                        </p>
                      </div>
                    </Col>
                  </Row>

                  <Collapse isOpen={selectedGroups.length > 0}>
                    <Row className="py-2">
                      <Col className="col-auto">
                        <Button
                          color="success"
                          disabled={selectedGroups.length == 0 || update}
                          onClick={() => {
                            linkGroups()
                          }}
                        >
                          <i className="mdi mdi-link-variant-plus me-2" />
                          {props.t("Link selected groups")}
                        </Button>
                        <Button
                          color="danger"
                          className="ms-3"
                          disabled={selectedGroups.length == 0 || update}
                          onClick={() => {
                            unlinkGroups()
                          }}
                        >
                          <i className="mdi mdi-link-variant-off me-2" />
                          {props.t("Unlink selected groups")}
                        </Button>
                      </Col>
                      <Col>
                        <p>
                          {selectedGroups.length +
                            " " +
                            props.t("selected groups")}
                        </p>
                      </Col>
                    </Row>
                  </Collapse>
                  <div className="mt-1">
                    <AntdTable
                      className="colored-header narrow-header"
                      bordered
                      size="small"
                      sticky
                      rowKey={"id"}
                      columns={columns}
                      dataSource={filteredGroups}
                      rowSelection={selectRow}
                      pagination={{
                        showSizeChanger: true,
                        defaultPageSize: 50,
                        pageSizeOptions: defaultPageSizeOptions,
                      }}
                      tableLayout="auto"
                      scroll={{
                        x: "max-content",
                        y: tableDefaultHeight,
                      }}
                    />
                  </div>
                </React.Fragment>
              </CardBody>
            </Card>
          </TabPane>

          <TabPane className="show" tabId="3">
            <Card>
              <CardBody>
                <React.Fragment>
                  <Row>
                    <Col className="col-auto pe-0">
                      <Search
                        placeholder={props.t("Search")}
                        allowClear
                        value={searchText2}
                        onChange={e => setSearchText2(e.target.value)}
                        style={{ width: "180px" }}
                      />
                    </Col>
                    <Col className="mt-1">
                      <FormGroup>
                        <Switch
                          className="me-2"
                          name="showAllClients"
                          checked={showAllClients}
                          onChange={v => {
                            setShowAllClients(v)
                          }}
                        ></Switch>
                        <Label>{props.t("Show all clients")}</Label>
                      </FormGroup>
                    </Col>
                    <Col>
                      <div className="mt-2 float-end">
                        <p>
                          {filteredPrivateClients.length} {props.t("clients")}
                        </p>
                      </div>
                    </Col>
                  </Row>
                  <Collapse isOpen={selectedClients.length > 0}>
                    <Row className="py-2">
                      <Col className="col-auto">
                        <Button
                          color="success"
                          disabled={selectedClients.length == 0 || update}
                          onClick={() => {
                            linkClients()
                          }}
                        >
                          <i className="mdi mdi-link-variant-plus me-2" />
                          {props.t("Link selected clients")}
                        </Button>
                        <Button
                          color="danger"
                          className="ms-3"
                          disabled={selectedClients.length == 0 || update}
                          onClick={() => {
                            unlinkClients()
                          }}
                        >
                          <i className="mdi mdi-link-variant-off me-2" />
                          {props.t("Unlink selected clients")}
                        </Button>
                      </Col>
                      <Col className="mt-2">
                        <p>
                          {selectedClients.length +
                            " " +
                            props.t("selected clients")}
                        </p>
                      </Col>
                    </Row>
                  </Collapse>
                  <div>
                    <AntdTable
                      className="colored-header narrow-header"
                      bordered
                      size="small"
                      sticky
                      rowKey={"key"}
                      columns={columns1}
                      dataSource={filteredPrivateClients}
                      rowSelection={selectRow2}
                      pagination={{
                        showSizeChanger: true,
                        defaultPageSize: 50,
                        pageSizeOptions: defaultPageSizeOptions,
                      }}
                      tableLayout="auto"
                      scroll={{
                        x: "max-content",
                        y: tableDefaultHeight,
                      }}
                    />
                  </div>
                </React.Fragment>
              </CardBody>
            </Card>
          </TabPane>

          <TabPane className="show" tabId="4">
            TODO
          </TabPane>
        </TabContent>
      </Drawer>

      <Modal
        isOpen={deleteModal}
        toggle={() => {
          setDeleteModal(!deleteModal)
        }}
        centered
      >
        <ModalHeader>{props.t("Delete confirmation")}</ModalHeader>
        <ModalBody>
          <p>
            {props.t("Are you sure you want to delete") +
              " " +
              props.selectedRow?.username +
              "?"}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            outline
            onClick={() => {
              setDeleteModal(false)
            }}
          >
            {props.t("Cancel")}
          </Button>
          <Button
            disabled={ongoingDelete}
            color="danger"
            onClick={() => {
              deleteAdmin()
            }}
          >
            {props.t("Delete")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

KeycloakUserDrawer.propTypes = {
  t: PropTypes.any,
  showDrawer: PropTypes.bool,
  setShowDrawer: PropTypes.any,
  selectedRow: PropTypes.any,
  getAdmins: PropTypes.func,
}

export default withRouter(withTranslation()(KeycloakUserDrawer))
